/**
 * Landing-page (non-flow) content surfaces — Cosmos `landing` container.
 *
 * One Cosmos doc per surface. Partition key `/id`.
 *
 * Known surfaces (Phase 3 launch set):
 *   - hero           — top-of-page banner + CTAs
 *   - about          — about-section copy + image
 *   - categories     — landing tile grid
 *   - contact        — footer / contact block (email, phone, address)
 *   - modals         — i18n-ish strings for in-app modal copy
 *   - email-template — order-confirmation email Subject + HTML body
 *   - defaults       — currency, min quantity, default quantity, VAT %
 *
 * Source of truth for migration: apps/public/src/App.tsx (Phase K+2 will
 * first extract these into JSON files; Phase 9 then upserts them into Cosmos).
 */

import { z } from 'zod';
import {
  CosmosBaseSchema,
  EntityIdSchema,
  IsoTimestampSchema,
  PublishStatusSchema,
} from './common.js';

// ---------------------------------------------------------------------------
// Surface enum
// ---------------------------------------------------------------------------

export const LandingSurfaceSchema = z.enum([
  'hero',
  'about',
  'categories',
  'contact',
  'modals',
  'email-template',
  'defaults',
  // Phase 9 — additional public-bundle landing surfaces extracted from App.tsx
  'build-your-own',
  'box-themes',
  'section-banners',
  'checkout-labels',
]);
export type LandingSurface = z.infer<typeof LandingSurfaceSchema>;

// ---------------------------------------------------------------------------
// Link / HTML safety (N4a Fix #4) — schema-level protocol allowlist
// ---------------------------------------------------------------------------
//
// A stored `href` or an `<a href>`/`src` inside an About paragraph could carry
// a `javascript:` / `data:` / `vbscript:` URL that becomes live XSS the moment
// the public renders it. We reject these at WRITE/validation time (schema
// level) so a bad value never persists — the public render bundle stays frozen
// (Constraint #1) and never needs to defend against a value the schema would
// have refused.

/**
 * Dangerous URL protocols that must never reach a rendered href/src.
 * Anything that can execute script or smuggle a payload.
 */
const DANGEROUS_PROTOCOLS = [
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
];

/**
 * True when `href` is a SAFE link target. Allowed:
 *   - http(s) absolute URLs
 *   - protocol-relative (`//host/...`)
 *   - root-relative (`/path`), relative (`path`), hash anchors (`#id`)
 *   - `mailto:` and `tel:` (legitimate CTA targets)
 * Rejected: javascript:, data:, vbscript:, file:, and any other scheme.
 *
 * The check is on the RAW string (post leading-whitespace + control-char
 * stripping) so obfuscations like `java\tscript:` or ` javascript:` are caught.
 */
export function isSafeHref(href: string): boolean {
  // Strip ALL whitespace + ASCII C0 control chars (U+0000-U+001F) + DEL
  // (U+007F). Browsers ignore embedded tabs/newlines/NULs when resolving
  // a URL scheme, so an obfuscated `java[TAB]script:` / `[NUL]javascript:`
  // is normalised before the scheme check. Then lowercase for comparison.
  // eslint-disable-next-line no-control-regex
  const cleaned = href.replace(/[\s\u0000-\u001f\u007f]+/g, '').toLowerCase();
  if (cleaned.length === 0) return false;
  for (const proto of DANGEROUS_PROTOCOLS) {
    if (cleaned.startsWith(proto)) return false;
  }
  // Reject any other explicit scheme that isn't http(s)/mailto/tel. A scheme is
  // `word:` before the first `/`, `?`, or `#`. Relative / root-relative / hash
  // hrefs have no scheme and are allowed.
  const schemeMatch = cleaned.match(/^([a-z][a-z0-9+.-]*):/);
  if (schemeMatch) {
    const scheme = schemeMatch[1]!;
    const ALLOWED_SCHEMES = ['http', 'https', 'mailto', 'tel'];
    if (!ALLOWED_SCHEMES.includes(scheme)) return false;
  }
  return true;
}

/**
 * A bounded string href that must pass `isSafeHref`. Rejects javascript:/data:
 * etc. at validation time so a malicious CTA target never persists.
 */
const SafeHrefSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine(isSafeHref, {
    message:
      'href must be http(s)/relative/hash/mailto/tel — javascript:, data:, vbscript: and file: are rejected',
  });

/**
 * True when a trusted-simple-HTML blob contains no dangerous href/src URL.
 * Scans for any `href`/`src` attribute whose value starts with a dangerous
 * protocol (after whitespace/control-char stripping) and rejects it. Also
 * rejects inline event handlers (`on*=`) and `<script>`/`<iframe>` tags which
 * the About paragraph allowlist (p/strong/em/br/a) never legitimately needs.
 */
export function isSafeSimpleHtml(html: string): boolean {
  const lower = html.toLowerCase();
  // 1. No <script>/<iframe>/<object>/<embed> tags.
  if (/<\s*(script|iframe|object|embed)\b/.test(lower)) return false;
  // 2. No inline event handlers (onerror=, onclick=, onload=, …).
  if (/\son[a-z]+\s*=/.test(lower)) return false;
  // 3. Any href/src attribute value must be a safe href.
  const attrRe = /(?:href|src)\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/gi;
  let m: RegExpExecArray | null;
  while ((m = attrRe.exec(html)) !== null) {
    const value = m[2] ?? m[3] ?? m[4] ?? '';
    if (!isSafeHref(value)) return false;
  }
  return true;
}

/** A bounded simple-HTML string that must pass `isSafeSimpleHtml`. */
function safeSimpleHtml(min: number, max: number) {
  return z
    .string()
    .min(min)
    .max(max)
    .refine(isSafeSimpleHtml, {
      message:
        'HTML must not contain javascript:/data: hrefs, inline event handlers, or <script>/<iframe> tags',
    });
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

export const CtaSchema = z
  .object({
    label: z.string().min(1).max(128),
    // N4a Fix #4: protocol-allowlisted href (rejects javascript:/data: at write).
    href: SafeHrefSchema,
    variant: z.enum(['primary', 'outline']),
  })
  .strict();
export type Cta = z.infer<typeof CtaSchema>;

export const HeroContentSchema = z
  .object({
    title: z.string().min(1).max(256),
    subtitle: z.string().max(512).optional().or(z.literal('')),
    ctas: z.array(CtaSchema).min(0).max(4),
  })
  .strict()
  .extend({
    subtitle: z.string().min(1).max(512),
  });
export type HeroContent = z.infer<typeof HeroContentSchema>;

// ---------------------------------------------------------------------------
// About
// ---------------------------------------------------------------------------

export const AboutContentSchema = z
  .object({
    eyebrow: z.string().min(1).max(128),
    titleLine1: z.string().min(1).max(256),
    titleLine2: z.string().min(1).max(256),
    titleLine3: z.string().min(1).max(256),
    /**
     * Trusted simple HTML. Permitted tags: p, strong, em, br, a (no inline JS).
     * N4a Fix #4: schema-level protocol allowlist — rejects javascript:/data:
     * hrefs, inline `on*=` event handlers, and `<script>`/`<iframe>` tags at
     * WRITE time, so a malicious paragraph never persists (the frozen public
     * render bundle never has to defend against it).
     */
    paragraph1Html: safeSimpleHtml(1, 8000),
    paragraph2Html: safeSimpleHtml(1, 8000),
    image: z.string().min(1).max(2048),
    imageAlt: z.string().min(1).max(512),
  })
  .strict();
export type AboutContent = z.infer<typeof AboutContentSchema>;

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export const CategorySchema = z
  .object({
    id: EntityIdSchema,
    label: z.string().min(1).max(128),
    image: z.string().min(1).max(2048),
    isAvailable: z.boolean(),
    order: z.number().int().nonnegative().optional(),
  })
  .strict();
export type Category = z.infer<typeof CategorySchema>;

export const CategoriesContentSchema = z
  .object({
    categories: z.array(CategorySchema).min(1),
  })
  .strict()
  .refine(
    (val) => new Set(val.categories.map((c) => c.id)).size === val.categories.length,
    { message: 'category ids must be unique' },
  );
export type CategoriesContent = z.infer<typeof CategoriesContentSchema>;

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export const ContactContentSchema = z
  .object({
    heading: z.string().min(1).max(256),
    email: z.string().email(),
    phone: z.string().min(1).max(64).optional(),
    addressLines: z.array(z.string().min(1).max(256)).min(1).max(8),
  })
  .strict();
export type ContactContent = z.infer<typeof ContactContentSchema>;

// ---------------------------------------------------------------------------
// Modals (key-value copy bag)
// ---------------------------------------------------------------------------

export const ModalEntrySchema = z
  .object({
    title: z.string().min(1).max(256),
    body: z.string().min(1).max(8000),
    cta: z.string().min(1).max(128).optional(),
  })
  .strict();
export type ModalEntry = z.infer<typeof ModalEntrySchema>;

export const ModalsContentSchema = z
  .object({
    modals: z.record(z.string().min(1), ModalEntrySchema),
  })
  .strict();
export type ModalsContent = z.infer<typeof ModalsContentSchema>;

// ---------------------------------------------------------------------------
// Email template
// ---------------------------------------------------------------------------

export const EmailTemplateContentSchema = z
  .object({
    subjectTemplate: z.string().min(1).max(512),
    bodyHtmlTemplate: z.string().min(1).max(20000),
    fromAddress: z.string().email(),
    /** Optional Reply-To address; falls back to fromAddress when omitted. */
    replyToAddress: z.string().email().optional(),
  })
  .strict();
export type EmailTemplateContent = z.infer<typeof EmailTemplateContentSchema>;

// ---------------------------------------------------------------------------
// Defaults (currency, quantity, VAT)
// ---------------------------------------------------------------------------

export const DefaultsContentSchema = z
  .object({
    currency: z.enum(['EUR']),
    minQuantity: z.number().int().positive(),
    defaultQuantity: z.number().int().positive(),
    vatPercent: z.number().min(0).max(100),
    /**
     * Site-wide delivery lead time in days (Chapter 10, F8). A box may override
     * it with its own `minLeadDays`; when neither is set the hard default is
     * `DEFAULT_MIN_LEAD_DAYS` (14).
     *
     * Optional because this object is `.strict()`: a stored defaults document
     * written before Chapter 10 has no such key and must keep validating.
     */
    minLeadDays: z.number().int().positive().max(365).optional(),
  })
  .strict()
  .refine((v) => v.defaultQuantity >= v.minQuantity, {
    message: 'defaultQuantity must be >= minQuantity',
    path: ['defaultQuantity'],
  });
export type DefaultsContent = z.infer<typeof DefaultsContentSchema>;

// ---------------------------------------------------------------------------
// Build-your-own (button grid section on landing)
// ---------------------------------------------------------------------------

export const BuildYourOwnButtonSchema = z
  .object({
    id: EntityIdSchema,
    label: z.string().min(1).max(128),
    isAvailable: z.boolean(),
  })
  .strict();
export type BuildYourOwnButton = z.infer<typeof BuildYourOwnButtonSchema>;

export const BuildYourOwnContentSchema = z
  .object({
    eyebrow: z.string().min(1).max(128),
    titleWord: z.string().min(1).max(128),
    titleSuffix: z.string().min(1).max(128),
    caption: z.string().min(1).max(512),
    buttons: z.array(BuildYourOwnButtonSchema).min(1).max(12),
  })
  .strict()
  .refine(
    (val) => new Set(val.buttons.map((b) => b.id)).size === val.buttons.length,
    { message: 'button ids must be unique' },
  );
export type BuildYourOwnContent = z.infer<typeof BuildYourOwnContentSchema>;

// ---------------------------------------------------------------------------
// Box themes (heading + description only — cards come from `categories`)
// ---------------------------------------------------------------------------

export const BoxThemesContentSchema = z
  .object({
    heading: z.string().min(1).max(256),
    description: z.string().min(1).max(2000),
  })
  .strict();
export type BoxThemesContent = z.infer<typeof BoxThemesContentSchema>;

// ---------------------------------------------------------------------------
// Section banners (Packaging / Merch / Final-Touch banners)
// ---------------------------------------------------------------------------

const SectionBannerEntrySchema = z
  .object({
    title: z.string().min(1).max(256),
    description: z.string().min(1).max(1024),
  })
  .strict();

export const SectionBannersContentSchema = z
  .object({
    banners: z
      .object({
        packaging: SectionBannerEntrySchema,
        merch: SectionBannerEntrySchema,
        'final-touch': SectionBannerEntrySchema,
      })
      .strict(),
  })
  .strict();
export type SectionBannersContent = z.infer<typeof SectionBannersContentSchema>;

// ---------------------------------------------------------------------------
// Checkout labels (form labels + placeholders on the checkout page)
// ---------------------------------------------------------------------------

export const CheckoutLabelsContentSchema = z
  .object({
    headerTitle: z.string().min(1).max(128),
    headerSubtitle: z.string().min(1).max(512),
    quantityHeading: z.string().min(1).max(128),
    storageHeading: z.string().min(1).max(128),
    shipmentHeading: z.string().min(1).max(128),
    logoUploadLabel: z.string().min(1).max(128),
    deliveryDateLabel: z.string().min(1).max(128),
    notesLabel: z.string().min(1).max(128),
    notesPlaceholder: z.string().min(1).max(256),
    companyPlaceholder: z.string().min(1).max(128),
    fullNamePlaceholder: z.string().min(1).max(128),
    emailPlaceholder: z.string().min(1).max(128),
    phonePlaceholder: z.string().min(1).max(128),
    /**
     * @deprecated RETIRED by Chapter 10 (F23). Renders nowhere.
     *
     * It labelled the checkout's submit button. P3b (F10/F11) collapsed the three
     * CTAs into "Review request" → "Send request", so the only submit button now
     * lives inside the review dialog and the page's button is a NAVIGATION step.
     *
     * Retired rather than repurposed: pointing this field at the review button
     * would mean someone editing a field called "submit label" silently changed a
     * navigation control. Use {@link CheckoutLabelsContentSchema.shape.reviewLabel}
     * and `sendLabel`.
     *
     * Kept, and made optional, purely so no migration is needed in either
     * direction: the document stored in Cosmos today still carries it and must
     * keep validating, and the seed no longer ships it.
     */
    submitLabel: z.string().min(1).max(128).optional(),
    /**
     * Chapter 10 (F23) — the checkout's ONE primary action, which opens the
     * itemised review. It does not send anything, which is why it is a separate
     * field from `sendLabel`.
     *
     * Optional so every pre-P4c document keeps validating; the public read falls
     * back to the canonical "Review request".
     */
    reviewLabel: z.string().min(1).max(128).optional(),
    /**
     * Chapter 10 (F23) — the button inside the review dialog that actually sends
     * the order. The dialog quotes this label back to the customer ("Nothing is
     * sent until you press …"), so it must name the real action.
     *
     * The failure variants ("Send without logos") stay derived in the component:
     * they depend on runtime state, not on authored copy.
     *
     * Optional so every pre-P4c document keeps validating; the public read falls
     * back to the canonical "Send request".
     */
    sendLabel: z.string().min(1).max(128).optional(),
    overviewLabel: z.string().min(1).max(128),
    estimatedTotalLabel: z.string().min(1).max(128),
    vatSuffix: z.string().min(1).max(64),
    newsletterLabel: z.string().min(1).max(256),
    /**
     * N6c — store-ruled STORAGE / SHIPMENT option lists. These used to be
     * hardcoded literals in the public CheckoutPage
     * (`['LONG TERM','SHORT TERM','DECIDE LATER']` /
     * `['NL','EUROPE','OTHER','DECIDE LATER']`) that round-trip into the
     * submission/email. Sourcing them here makes them admin/seed-editable.
     *
     * OPTIONAL so every pre-N6c `checkout-labels` doc still validates; the
     * public read falls back to the canonical defaults when absent, keeping the
     * rendered options + the submitted values byte-identical. Each option is a
     * short bounded string; the list must be non-empty when present.
     */
    storageOptions: z.array(z.string().min(1).max(64)).min(1).max(12).optional(),
    shipmentOptions: z.array(z.string().min(1).max(64)).min(1).max(12).optional(),
  })
  .strict();
export type CheckoutLabelsContent = z.infer<typeof CheckoutLabelsContentSchema>;

// ---------------------------------------------------------------------------
// Discriminated union of all surface payloads
// ---------------------------------------------------------------------------

const surfacePayload = z.discriminatedUnion('surface', [
  z.object({ surface: z.literal('hero'), data: HeroContentSchema }),
  z.object({ surface: z.literal('about'), data: AboutContentSchema }),
  z.object({ surface: z.literal('categories'), data: CategoriesContentSchema }),
  z.object({ surface: z.literal('contact'), data: ContactContentSchema }),
  z.object({ surface: z.literal('modals'), data: ModalsContentSchema }),
  z.object({
    surface: z.literal('email-template'),
    data: EmailTemplateContentSchema,
  }),
  z.object({ surface: z.literal('defaults'), data: DefaultsContentSchema }),
  z.object({
    surface: z.literal('build-your-own'),
    data: BuildYourOwnContentSchema,
  }),
  z.object({ surface: z.literal('box-themes'), data: BoxThemesContentSchema }),
  z.object({
    surface: z.literal('section-banners'),
    data: SectionBannersContentSchema,
  }),
  z.object({
    surface: z.literal('checkout-labels'),
    data: CheckoutLabelsContentSchema,
  }),
]);

// ---------------------------------------------------------------------------
// Landing document
// ---------------------------------------------------------------------------

const landingMetaSchema = CosmosBaseSchema.extend({
  docType: z.literal('landing'),
  status: PublishStatusSchema,
  version: z.number().int().nonnegative(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  updatedBy: z.string().email(),
});

/**
 * Combined schema: Cosmos base + the discriminated surface payload.
 * Use this for any read/write of a `landing` container document.
 */
export const LandingDocSchema = z.intersection(
  landingMetaSchema,
  surfacePayload,
);
export type LandingDoc = z.infer<typeof LandingDocSchema>;

/**
 * N4a Fix #8 (GDPR) — PUBLIC-read projection of a landing doc. Identical to
 * `LandingDocSchema` except `updatedBy` (an editor email) is OPTIONAL, because
 * the public CDN snapshot STRIPS it (see
 * `packages/data-access/src/snapshot/buildContentSnapshot.ts`). Admin/Cosmos
 * read+write keep using the strict `LandingDocSchema`; only the public bundle
 * validates the PII-stripped snapshot against this. Extends the existing
 * schema (reuses `landingMetaSchema` + `surfacePayload`) — not a fork.
 */
export const PublicLandingDocSchema = z.intersection(
  landingMetaSchema.extend({ updatedBy: z.string().email().optional() }),
  surfacePayload,
);
export type PublicLandingDoc = z.infer<typeof PublicLandingDocSchema>;

/** Convenience aggregate shape returned by `GET /api/landing`. */
export interface SiteContent {
  hero: HeroContent;
  about: AboutContent;
  categories: Category[];
  contact?: ContactContent;
  modals?: ModalsContent;
  emailTemplate?: EmailTemplateContent;
  defaults?: DefaultsContent;
}
